import type { Campaign } from '../../../api/fetchers/promotionsV2/types/campaign';
import { CampaignType } from '../../../api/fetchers/promotionsV2/types/campaign';
import type {
  PrizedropPrizeDetails,
  Promotion,
  TournamentPrizeDetails,
} from '../../../api/fetchers/promotionsV2/types/promotions';
import { PromoRoundType, PromotionType } from '../../../api/fetchers/promotionsV2/types/promotions';
import type { Trophy } from '../../../store/slices/promotionsV2/types';
import { CoBrandProms, PromoBackgroundModifiers, TimerStatusType } from '../types';

/* 
  Get promo background modifier
*/
export const getPromoBgColor = (brandName: string): PromoBackgroundModifiers | string => {
  switch (brandName.toUpperCase()) {
    case CoBrandProms.DDW: {
      return PromoBackgroundModifiers.Blue;
    }
    case CoBrandProms.BronzeBlackJack:
    case CoBrandProms.SilverBlackJack:
    case CoBrandProms.GoldBlackJack:
    case CoBrandProms.PrivateBlackJack: {
      return brandName;
    }
    default: {
      return PromoBackgroundModifiers.Grey;
    }
  }
};

export const getIsPromoStarted = (status: TimerStatusType): boolean => {
  return status !== TimerStatusType.STARTING;
};

export const getPrizes = (promotion: Promotion): TournamentPrizeDetails['prizes'] | PrizedropPrizeDetails['prizes'] => {
  switch (promotion.type) {
    case PromotionType.Tournament: {
      return (promotion.prizeDetails as unknown as TournamentPrizeDetails).prizes;
    }
    case PromotionType.Prizedrop: {
      return (promotion.prizeDetails as unknown as PrizedropPrizeDetails).prizes;
    }
    default: {
      return [];
    }
  }
};

export const scalePopUp = (wrapper: HTMLElement): void => {
  const modalContainer = wrapper.querySelector('div');
  if (!modalContainer) return;
  const scale = Math.max(
    1,
    modalContainer.clientWidth / wrapper.offsetWidth,
    modalContainer.clientHeight / wrapper.offsetHeight
  );
  modalContainer.style.transform = scale > 1 ? `scale(${1 / scale - 0.2})` : '';
  modalContainer.style.transition = 'transform .3s ease-in-out';
};

export const getRoundLimitMessage = (roundType: PromoRoundType | null): string => {
  switch (roundType) {
    case PromoRoundType.Daily:
      return 'DAILY_TOURNAMENT_LIMIT';
    case PromoRoundType.Total:
      return 'TOTAL_TOURNAMENT_LIMIT';
    default:
      return '';
  }
};

export const mapToNewTrophy = (oldTrophy: {
  minBet: string;
  showTrophyTooltip: boolean;
  isRoundLimitExceed: boolean;
  visibility: 'active' | 'inactive' | 'none';
  roundLimitExceededMessage: string;
}): Trophy => {
  return {
    enable: ['active', 'inactive'].includes(oldTrophy.visibility),
    isActive: oldTrophy.visibility === 'active',
    toolTipContent: oldTrophy.isRoundLimitExceed ? oldTrophy.roundLimitExceededMessage : 'PROMO_BET_BELOWMIN',
    minBet: oldTrophy.minBet,
  };
};

export const sortByEarlyExpiry = (promotions: Campaign[]): Campaign[] => {
  const sortedPromotionsByExpiryTime = [...promotions].sort((a, b) => {
    const e1 = a.campaignType === CampaignType.BonusPromo ? a.bonus.claimEndsIn : a.activeDuration.toEpoch;
    const e2 = b.campaignType === CampaignType.BonusPromo ? b.bonus.claimEndsIn : b.activeDuration.toEpoch;

    return e1 > e2 ? 1 : -1;
  });
  return sortedPromotionsByExpiryTime;
};

export function roundCurrency(value: number): number {
  if (value < 0.1) {
    return value;
  }
  if (value < 1) {
    return Math.floor(value * 10) / 10;
  }
  if (value < 10) {
    return Math.floor(value);
  }
  const factor = Math.pow(10, Math.floor(Math.log10(value)) - 1); // Define the rounding factor (e.g., 10, 100, 1000)
  return Math.floor(value / factor) * factor; // Round down based on the factor
}

export const SCREEN_NAME_FOR_LOGGED_IN_USER = 'YOU';
